import axios from 'axios';
import { config } from '../config/env';
import { redirectToLogin } from '../api/auth-handler';
import { refreshAccessToken } from '../api/tokenRefresh';

class NotificationService {
  constructor() {
    this.baseURL = `${config.API_BASE_URL}/notifications`;
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 10000, // 10 second timeout
      withCredentials: true // Include auth cookies
    });

    // Add auth token to requests
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Add response interceptor to handle 401 errors
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response && error.response.status === 401) {
          console.warn('🔒 401 Unauthorized - Session expired or invalid');

          // JWT_EXPIRED = the access JWT lapsed but the session is still valid.
          // Silently re-mint and retry once; only a dead session redirects to login.
          const original = error.config;
          const code = error.response.headers?.['x-error-code'];
          if (code === 'JWT_EXPIRED' && original && !original._retry) {
            original._retry = true;
            if (await refreshAccessToken()) {
              return this.client(original);
            }
          }

          // Centralized redirect-to-login (clears session, tags product=athena).
          redirectToLogin();

          // Return a rejected promise to stop further execution
          return Promise.reject(new Error('Authentication required - redirecting to login'));
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Get notification statistics (unread counts by category)
   * LIGHTWEIGHT QUERY - Used for background polling every 30s
   */
  async getNotificationStats() {
    try {
      const response = await this.client.get('/stats');
      return response.data;
      // Returns: { total: 5, THREAT: 2, VULNERABILITY: 1, WORKFLOW: 2, SYSTEM: 0 }
    } catch (error) {
      console.error('Error fetching notification stats:', error);
      throw error;
    }
  }

  /**
   * Get notifications with filters
   * HEAVIER QUERY - Used when dropdown opens (every 10s while open)
   */
  async getNotifications({ category, is_read, limit = 50, offset = 0 }) {
    try {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (is_read !== null && is_read !== undefined) params.append('is_read', is_read);
      params.append('limit', limit);
      params.append('offset', offset);

      const response = await this.client.get(`?${params.toString()}`);
      return response.data;
      // Returns: { notifications: [...], total: 50, unread_count: 5 }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  /**
   * Mark single notification as read
   */
  async markNotificationAsRead(notificationId) {
    try {
      const response = await this.client.post(`/${notificationId}/read`);
      return response.data;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  /**
   * Mark multiple notifications as read
   */
  async markMultipleAsRead(notificationIds) {
    try {
      const response = await this.client.post('/mark-read', {
        notification_ids: notificationIds
      });
      return response.data;
    } catch (error) {
      console.error('Error marking multiple notifications as read:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read (optionally filtered by category)
   */
  async markAllAsRead(category = null) {
    try {
      const params = category ? `?category=${category}` : '';
      const response = await this.client.post(`/mark-all-read${params}`);
      return response.data;
    } catch (error) {
      console.error('Error marking all as read:', error);
      throw error;
    }
  }

  /**
   * Delete notification
   */
  async deleteNotification(notificationId) {
    try {
      const response = await this.client.delete(`/${notificationId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }
}

export const notificationService = new NotificationService();
